import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Mail, X } from 'lucide-react';
import {
  INVITABLE_ROLES,
  ROLE_DESCRIPTIONS,
  ROLE_LABELS,
  can,
  rolesInvitableBy,
  type AuditEntry,
  type InvitableRole,
  type TeamDetail,
  type TeamInvite,
  type TeamMember,
} from '@hoopify/shared';
import { api, errorMessage, fieldErrors } from '../lib/api';
import { useMe } from '../lib/session';
import { formatDate, initials, timeAgo } from '../lib/format';
import { Alert, Button, Dialog, Empty, Field, LoadingBlock, RoleBadge, Section } from '../components/ui';
import { TeamSessions } from '../components/sessions/TeamSessions';
import { SeasonTab } from '../components/season/SeasonTab';
import { RosterTab } from '../components/roster/RosterTab';
import { TeamReminderSettings, TimeZoneSelect } from '../components/reminders/Reminders';
import { JoinLinkSection, JoinRequestsSection } from '../components/join/JoinTeam';
import { LadderTab, TeamClubSection } from '../components/clubs/TeamClub';

type Tab = 'sessions' | 'season' | 'ladder' | 'roster' | 'people' | 'activity' | 'settings';

export function TeamPage() {
  const { teamId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const team = useQuery({ queryKey: ['team', teamId], queryFn: () => api.get<TeamDetail>(`/teams/${teamId}`) });

  if (team.isLoading) return <LoadingBlock />;
  if (team.isError || !team.data) {
    return (
      <div className="space-y-4">
        <Alert>{errorMessage(team.error)}</Alert>
        <Link to="/teams" className="btn-secondary">
          Back to teams
        </Link>
      </div>
    );
  }

  const t = team.data;
  const tabs: { id: Tab; label: string }[] = [
    { id: 'sessions', label: 'Sessions' },
    ...(can(t.role, 'sessions:details') ? [{ id: 'season' as Tab, label: 'Season' }] : []),
    ...(t.playhq ? [{ id: 'ladder' as Tab, label: 'Ladder' }] : []),
    ...(can(t.role, 'members:read') ? [{ id: 'roster' as Tab, label: 'Roster' }] : []),
    { id: 'people', label: 'People' },
    ...(can(t.role, 'audit:read') ? [{ id: 'activity' as Tab, label: 'Activity' }] : []),
    { id: 'settings', label: 'Settings' },
  ];
  const requested = params.get('tab') as Tab | null;
  const tab: Tab = tabs.some((x) => x.id === requested) ? requested! : 'sessions';

  return (
    <>
      <Link to="/teams" className="mb-4 inline-flex items-center gap-1.5 text-[14px] text-muted hover:text-ink">
        <ArrowLeft className="size-4" aria-hidden /> Teams
      </Link>
      <header className="mb-6">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="text-[24px] leading-tight sm:text-[28px]">{t.name}</h1>
          <RoleBadge role={t.role} />
        </div>
        {(t.ageGroup || t.season || t.club) && <p className="mt-1 text-muted">{[t.ageGroup, t.season, t.club?.name].filter(Boolean).join(', ')}</p>}
      </header>

      <nav className="mb-6 -mx-4 flex gap-1 overflow-x-auto border-b border-line px-4 sm:mx-0 sm:px-0" aria-label="Team sections">
        {tabs.map((x) => (
          <button
            key={x.id}
            onClick={() => setParams(x.id === 'sessions' ? {} : { tab: x.id }, { replace: true })}
            className={`-mb-px shrink-0 border-b-2 px-3 py-2 text-[14px] font-medium whitespace-nowrap ${tab === x.id ? 'border-action text-ink' : 'border-transparent text-muted hover:text-ink'}`}
            aria-current={tab === x.id ? 'page' : undefined}
          >
            {x.label}
          </button>
        ))}
      </nav>

      {tab === 'sessions' && <TeamSessions team={t} />}
      {tab === 'season' && <SeasonTab team={t} />}
      {tab === 'ladder' && <LadderTab team={t} />}
      {tab === 'roster' && <RosterTab team={t} />}
      {tab === 'people' && <PeopleTab team={t} />}
      {tab === 'activity' && <ActivityTab teamId={t.id} />}
      {tab === 'settings' && <SettingsTab team={t} />}
    </>
  );
}

// ---------- People ----------

function PeopleTab({ team }: { team: TeamDetail }) {
  const canInvite = rolesInvitableBy(team.role).length > 0;
  const canSeeMembers = can(team.role, 'members:read');
  const canApprove = can(team.role, 'invites:players');
  return (
    <div>
      {canApprove && <JoinRequestsSection team={team} />}
      {canApprove && <JoinLinkSection team={team} />}
      {canInvite && (
        <Section title="Invite someone by email" description="They’ll get an email with a link to join. Invites work for 7 days.">
          <InviteForm team={team} />
        </Section>
      )}
      {canInvite && <PendingInvites team={team} />}
      {canSeeMembers ? (
        <Members team={team} />
      ) : (
        <Section title="Members">
          <p className="text-muted">Only coaches can see the member list.</p>
        </Section>
      )}
    </div>
  );
}

function InviteForm({ team }: { team: TeamDetail }) {
  const qc = useQueryClient();
  const roles = rolesInvitableBy(team.role);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<InvitableRole>(roles[0]!);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const invite = useMutation({
    mutationFn: () => api.post<TeamInvite>(`/teams/${team.id}/invites`, { email, role }),
    onSuccess: (inv) => {
      setSentTo(inv.email);
      setEmail('');
      void qc.invalidateQueries({ queryKey: ['invites', team.id] });
    },
  });
  const fields = fieldErrors(invite.error);

  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); setSentTo(null); invite.mutate(); }} className="space-y-3" noValidate>
      <div className="grid gap-3 sm:grid-cols-[1fr_200px_auto] sm:items-end">
        <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} error={fields.email} placeholder="name@example.com" required />
        <div className="space-y-1.5">
          <label htmlFor="invite-role" className="block text-[14px] font-medium">
            Role
          </label>
          <select id="invite-role" className="field-input" value={role} onChange={(e) => setRole(e.target.value as InvitableRole)}>
            {roles.map((r) => (
              <option key={r} value={r}>
                {ROLE_LABELS[r]}
              </option>
            ))}
          </select>
        </div>
        <Button type="submit" loading={invite.isPending} className={`btn-primary ${fields.email ? 'sm:mb-[26px]' : ''}`}>
          <Mail className="size-4" aria-hidden /> Send invite
        </Button>
      </div>
      <p className="text-[13px] text-muted">{ROLE_DESCRIPTIONS[role]}</p>
      {invite.isError && !fields.email && <Alert>{errorMessage(invite.error)}</Alert>}
      {sentTo && <Alert tone="ok">Invite sent to {sentTo}.</Alert>}
    </form>
  );
}

function PendingInvites({ team }: { team: TeamDetail }) {
  const qc = useQueryClient();
  const invites = useQuery({ queryKey: ['invites', team.id], queryFn: () => api.get<TeamInvite[]>(`/teams/${team.id}/invites`) });
  const revoke = useMutation({
    mutationFn: (id: string) => api.del(`/teams/${team.id}/invites/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invites', team.id] }),
  });
  const allowed = rolesInvitableBy(team.role) as string[];
  if (!invites.data?.length) return null;

  return (
    <Section title="Waiting to join" description="Invites that haven’t been accepted yet.">
      {revoke.isError && <div className="mb-3"><Alert>{errorMessage(revoke.error)}</Alert></div>}
      <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
        {invites.data.map((inv) => (
          <li key={inv.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
            <div className="min-w-0 basis-full sm:flex-1 sm:basis-auto">
              <p className="truncate font-medium">{inv.email}</p>
              <p className="text-[13px] text-muted">
                Invited by {inv.invitedBy} {timeAgo(inv.createdAt)}. Expires {formatDate(inv.expiresAt)}.
              </p>
            </div>
            <RoleBadge role={inv.role} />
            {allowed.includes(inv.role) && (
              <Button variant="ghost" className="btn-ghost px-2" onClick={() => revoke.mutate(inv.id)} loading={revoke.isPending && revoke.variables === inv.id} aria-label={`Cancel invite for ${inv.email}`}>
                <X className="size-4" aria-hidden /> Cancel
              </Button>
            )}
          </li>
        ))}
      </ul>
    </Section>
  );
}

function Members({ team }: { team: TeamDetail }) {
  const me = useMe();
  const qc = useQueryClient();
  const manage = can(team.role, 'members:manage');
  const [removing, setRemoving] = useState<TeamMember | null>(null);
  const members = useQuery({ queryKey: ['members', team.id], queryFn: () => api.get<TeamMember[]>(`/teams/${team.id}/members`) });

  const changeRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: InvitableRole }) => api.patch<TeamMember[]>(`/teams/${team.id}/members/${userId}`, { role }),
    onSuccess: (list) => qc.setQueryData(['members', team.id], list),
  });
  const remove = useMutation({
    mutationFn: (userId: string) => api.del(`/teams/${team.id}/members/${userId}`),
    onSuccess: async () => {
      setRemoving(null);
      await qc.invalidateQueries({ queryKey: ['members', team.id] });
      await qc.invalidateQueries({ queryKey: ['team', team.id] });
    },
  });

  return (
    <Section title="Members" description={members.data ? `${members.data.length} ${members.data.length === 1 ? 'person' : 'people'} on this team.` : undefined}>
      {members.isLoading ? (
        <LoadingBlock />
      ) : members.isError ? (
        <Alert>{errorMessage(members.error)}</Alert>
      ) : members.data!.length === 1 ? (
        <Empty title="It’s just you so far">Invite your assistant coaches, then your players and their parents.</Empty>
      ) : null}
      {changeRole.isError && <div className="mb-3"><Alert>{errorMessage(changeRole.error)}</Alert></div>}
      {members.data && members.data.length > 1 && (
        <ul className="divide-y divide-line rounded-lg border border-line bg-surface">
          {members.data.map((m) => {
            const editable = manage && m.role !== 'head_coach';
            return (
              <li key={m.userId} className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
                <span className="grid size-9 shrink-0 place-items-center rounded-full bg-sunken text-[12px] font-semibold text-muted" aria-hidden>
                  {initials(m.name)}
                </span>
                <div className="min-w-0 basis-[calc(100%-52px)] sm:flex-1 sm:basis-auto">
                  <p className="truncate font-medium">
                    {m.name}
                    {m.userId === me.id && <span className="font-normal text-muted"> (you)</span>}
                  </p>
                  <p className="truncate text-[13px] text-muted">{m.email}</p>
                </div>
                {editable ? (
                  <div className="ml-[52px] flex items-center gap-1 sm:ml-0">
                    <label className="sr-only" htmlFor={`role-${m.userId}`}>
                      Role for {m.name}
                    </label>
                    <select
                      id={`role-${m.userId}`}
                      className="field-input w-auto py-1.5 text-[14px]"
                      value={m.role}
                      onChange={(e) => changeRole.mutate({ userId: m.userId, role: e.target.value as InvitableRole })}
                      disabled={changeRole.isPending}
                    >
                      {INVITABLE_ROLES.map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABELS[r]}
                        </option>
                      ))}
                    </select>
                    <button className="btn-ghost p-2" onClick={() => setRemoving(m)} aria-label={`Remove ${m.name}`} title="Remove from team">
                      <X className="size-4" />
                    </button>
                  </div>
                ) : (
                  <span className="ml-[52px] sm:ml-0">
                    <RoleBadge role={m.role} />
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <Dialog open={!!removing} onClose={() => { setRemoving(null); remove.reset(); }} title={`Remove ${removing?.name ?? ''}?`}>
        <p className="text-muted">They’ll lose access to {team.name} straight away. You can invite them again later.</p>
        {remove.isError && <div className="mt-3"><Alert>{errorMessage(remove.error)}</Alert></div>}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setRemoving(null)}>
            Keep them
          </Button>
          <Button variant="danger" loading={remove.isPending} onClick={() => removing && remove.mutate(removing.userId)}>
            Remove
          </Button>
        </div>
      </Dialog>
    </Section>
  );
}

// ---------- Activity ----------

function ActivityTab({ teamId }: { teamId: string }) {
  const log = useQuery({ queryKey: ['audit', teamId], queryFn: () => api.get<AuditEntry[]>(`/teams/${teamId}/audit`) });
  if (log.isLoading) return <LoadingBlock />;
  if (log.isError) return <Alert>{errorMessage(log.error)}</Alert>;
  return (
    <Section title="Activity" description="Every change to this team, newest first. Only the head coach can see this.">
      <ol className="space-y-0">
        {log.data!.map((e) => (
          <li key={e.id} className="grid grid-cols-[1fr_auto] gap-4 border-b border-line py-2.5 last:border-b-0">
            <span>{e.summary}</span>
            <time dateTime={e.createdAt} className="tnum text-[13px] whitespace-nowrap text-muted" title={new Date(e.createdAt).toLocaleString('en-AU')}>
              {timeAgo(e.createdAt)}
            </time>
          </li>
        ))}
      </ol>
    </Section>
  );
}

// ---------- Settings ----------

function SettingsTab({ team }: { team: TeamDetail }) {
  return can(team.role, 'team:manage') ? (
    <>
      <TeamReminderSettings team={team} />
      <EditTeam team={team} />
      <TeamClubSection team={team} />
      <ArchiveTeam team={team} />
    </>
  ) : (
    <>
      <TeamReminderSettings team={team} />
      <LeaveTeam team={team} />
    </>
  );
}

function EditTeam({ team }: { team: TeamDetail }) {
  const qc = useQueryClient();
  const [name, setName] = useState(team.name);
  const [ageGroup, setAgeGroup] = useState(team.ageGroup ?? '');
  const [season, setSeason] = useState(team.season ?? '');
  const [timeZone, setTimeZone] = useState<string>(team.timeZone);
  const save = useMutation({
    mutationFn: () => api.patch<TeamDetail>(`/teams/${team.id}`, { name, ageGroup, season, timeZone }),
    onSuccess: (t) => {
      qc.setQueryData(['team', team.id], t);
      void qc.invalidateQueries({ queryKey: ['teams'] });
      void qc.invalidateQueries({ queryKey: ['reminder', team.id] });
    },
  });
  const fields = fieldErrors(save.error);
  const dirty = name !== team.name || ageGroup !== (team.ageGroup ?? '') || season !== (team.season ?? '') || timeZone !== team.timeZone;

  return (
    <Section title="Team details">
      <form onSubmit={(e: FormEvent) => { e.preventDefault(); save.mutate(); }} className="max-w-[520px] space-y-4" noValidate>
        <Field label="Team name" value={name} onChange={(e) => setName(e.target.value)} error={fields.name} required />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Age group" value={ageGroup} onChange={(e) => setAgeGroup(e.target.value)} error={fields.ageGroup} />
          <Field label="Season" value={season} onChange={(e) => setSeason(e.target.value)} error={fields.season} />
        </div>
        <TimeZoneSelect value={timeZone} onChange={setTimeZone} />
        {save.isError && !Object.keys(fields).length && <Alert>{errorMessage(save.error)}</Alert>}
        <div className="flex items-center gap-3">
          <Button type="submit" loading={save.isPending} disabled={!dirty}>
            Save changes
          </Button>
          {save.isSuccess && !dirty && <span className="text-[14px] text-ok">Saved</span>}
        </div>
      </form>
    </Section>
  );
}

function ArchiveTeam({ team }: { team: TeamDetail }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const archive = useMutation({
    mutationFn: () => api.del(`/teams/${team.id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['teams'] });
      navigate('/teams');
    },
  });
  return (
    <Section title="Archive team" description="Hides the team from everyone on it. Use this at the end of a season.">
      <Button variant="danger" onClick={() => setOpen(true)}>
        Archive {team.name}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} title={`Archive ${team.name}?`}>
        <p className="text-muted">Everyone on the team, including you, will stop seeing it. Its history is kept.</p>
        {archive.isError && <div className="mt-3"><Alert>{errorMessage(archive.error)}</Alert></div>}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Keep team
          </Button>
          <Button variant="danger" loading={archive.isPending} onClick={() => archive.mutate()}>
            Archive
          </Button>
        </div>
      </Dialog>
    </Section>
  );
}

function LeaveTeam({ team }: { team: TeamDetail }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const leave = useMutation({
    mutationFn: () => api.post(`/teams/${team.id}/leave`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['teams'] });
      navigate('/teams');
    },
  });
  return (
    <Section title="Leave team" description={`You’re on ${team.name} as ${ROLE_LABELS[team.role].toLowerCase()}. Only the head coach can change team details.`}>
      <Button variant="danger" onClick={() => setOpen(true)}>
        Leave {team.name}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} title={`Leave ${team.name}?`}>
        <p className="text-muted">You’ll lose access straight away. To come back, the coach will need to invite you again.</p>
        {leave.isError && <div className="mt-3"><Alert>{errorMessage(leave.error)}</Alert></div>}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="secondary" onClick={() => setOpen(false)}>
            Stay
          </Button>
          <Button variant="danger" loading={leave.isPending} onClick={() => leave.mutate()}>
            Leave team
          </Button>
        </div>
      </Dialog>
    </Section>
  );
}
